import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Cadence Config (docs/design/me-mockup.html): business logic as editable data —
// change a number, no deploy. The whole "automations are configuration" idea.
// Reads/writes the config table; shows the seeded automation rules (read-only here).
// Also hosts the admin-editable Acquisition deal stages (D27).

const DealStagesEditor = () => {
  const notify = useNotify();
  const [stages, setStages] = useState<any[]>([]);
  const [newName, setNewName] = useState("");
  const load = () => getSupabaseClient().from("deal_stages").select("*").order("position").then(({ data }) => setStages(data ?? []));
  useEffect(() => { load(); }, []);

  const patch = async (id: string, p: any) => {
    const { error } = await getSupabaseClient().from("deal_stages").update(p).eq("id", id);
    if (error) notify(`Failed: ${error.message}`, { type: "error" }); else load();
  };
  const add = async () => {
    if (!newName.trim()) return;
    const key = newName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const pos = Math.max(0, ...stages.filter((s) => !s.is_terminal).map((s) => s.position)) + 1;
    const { error } = await getSupabaseClient().from("deal_stages").insert({ key, name: newName.trim(), position: pos });
    if (error) notify(`Failed: ${error.message}`, { type: "error" }); else { setNewName(""); load(); notify("Stage added", { type: "success" }); }
  };

  return (
    <Card className="mt-4">
      <CardHeader><CardTitle className="text-base">Acquisition deal stages <span className="text-xs font-normal text-muted-foreground">(admin-editable — stages are data)</span></CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {stages.map((s) => (
          <div key={s.id} className="flex items-center gap-2">
            <Input className="w-16" type="number" defaultValue={s.position} onBlur={(e) => +e.target.value !== s.position && patch(s.id, { position: +e.target.value })} title="order" />
            <Input className="flex-1" defaultValue={s.name} onBlur={(e) => e.target.value !== s.name && patch(s.id, { name: e.target.value })} />
            {s.is_terminal && <Badge variant="outline">terminal</Badge>}
          </div>
        ))}
        <div className="flex items-center gap-2 pt-2">
          <Input className="flex-1" placeholder="Add a stage…" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button size="sm" onClick={add}>Add stage</Button>
        </div>
        <div className="text-xs text-muted-foreground">Reorder by editing the position numbers; rename inline. "Lost" is terminal (recycles the lead to marketing).</div>
      </CardContent>
    </Card>
  );
};

type Cfg = Record<string, any>;

const ConfigPage = () => {
  const notify = useNotify();
  const [cfg, setCfg] = useState<Cfg>({});
  const [rules, setRules] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSupabaseClient().from("config").select("key,value").then(({ data }) => {
      const m: Cfg = {}; (data ?? []).forEach((r: any) => (m[r.key] = r.value)); setCfg(m);
    });
    getSupabaseClient().from("automation_rules").select("name,trigger,enabled,priority").order("priority")
      .then(({ data }) => setRules(data ?? []));
  }, []);

  const setVal = (key: string, value: any) => setCfg((c) => ({ ...c, [key]: value }));

  const save = async () => {
    setSaving(true);
    const updates = Object.entries(cfg).map(([key, value]) =>
      getSupabaseClient().from("config").update({ value, updated_at: new Date().toISOString() }).eq("key", key));
    const results = await Promise.all(updates);
    setSaving(false);
    const err = results.find((r) => r.error);
    notify(err ? `Save failed: ${err.error!.message}` : "Config saved — applies immediately", { type: err ? "error" : "success" });
  };

  const cad = cfg.cadence_days ?? {};
  const react = cfg.reactivation_days ?? {};

  return (
    <div className="p-4 max-w-3xl">
      <h1 className="text-xl font-semibold">Cadence Config</h1>
      <div className="text-sm text-muted-foreground mb-4">Business logic as editable data — change a number, no deploy.</div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Follow-up cadence (days)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(["hot", "warm", "cold"] as const).map((k) => (
              <div key={k} className="flex items-center gap-2">
                <span className="capitalize w-16 text-sm">{k}</span>
                <Input className="w-20" type="number" value={cad[k] ?? ""} onChange={(e) => setVal("cadence_days", { ...cad, [k]: +e.target.value })} />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Attempt caps & mail</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Row label="Call-attempt cap"><Input className="w-20" type="number" value={cfg.marketing_call_attempt_cap ?? ""} onChange={(e) => setVal("marketing_call_attempt_cap", +e.target.value)} /></Row>
            <Row label="SMS-attempt cap"><Input className="w-20" type="number" value={cfg.marketing_sms_attempt_cap ?? ""} onChange={(e) => setVal("marketing_sms_attempt_cap", +e.target.value)} /></Row>
            <Row label="Mail spacing (days)"><Input className="w-20" type="number" value={cfg.mail_spacing_days ?? ""} onChange={(e) => setVal("mail_spacing_days", +e.target.value)} /></Row>
            <Row label="Mail max pieces"><Input className="w-20" type="number" value={cfg.mail_max_pieces ?? ""} onChange={(e) => setVal("mail_max_pieces", +e.target.value)} /></Row>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Cost-per-contract</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Row label="Marketing spend (MTD, $)">
              <Input className="w-28" type="number" placeholder="not set"
                value={cfg.marketing_spend_mtd ?? ""}
                onChange={(e) => setVal("marketing_spend_mtd", e.target.value === "" ? null : +e.target.value)} />
            </Row>
            <div className="text-xs text-muted-foreground">
              Admin-entered month-to-date marketing spend. Cost-per-contract = spend ÷ contracts. Leave blank → the KPI shows "cost source pending" (not fabricated). Per-activity cost tracking is a future upgrade.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Reactivation timers (days)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {["foreclosure", "probate", "general"].map((k) => (
              <Row key={k} label={k}><Input className="w-20" type="number" value={react[k] ?? ""} onChange={(e) => setVal("reactivation_days", { ...react, [k]: +e.target.value })} /></Row>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Mail rotation</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <div className="text-muted-foreground mb-1">piece = rotation[mail_step mod length]</div>
            {(cfg.mail_rotation ?? []).map((p: string, i: number) => (
              <div key={i} className="capitalize">{i + 1}. {p.replace(/_/g, " ")}</div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader><CardTitle className="text-base">Automation rules (the engine)</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          {rules.map((r) => (
            <div key={r.name} className="flex items-center justify-between text-sm border-b py-1 last:border-0">
              <span>{r.name} <span className="text-muted-foreground text-xs">· on {r.trigger.replace("_", " ")}</span></span>
              <Badge className={r.enabled ? "bg-green-100 text-green-800" : "bg-muted"}>{r.enabled ? "on" : "off"}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      <Button className="mt-4" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save changes"}</Button>

      <DealStagesEditor />
    </div>
  );
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-sm capitalize">{label}</span>{children}
  </div>
);

ConfigPage.path = "/config";
export default ConfigPage;
